import React, { useState, useCallback } from "react"
import styled from "styled-components"
import { ScrollLink } from "../elements"
import Logo from "../../images/logo.png"
// import BookIcon from "../../images/icons/viewing-icon.png"

const HeaderContainer = styled.div`
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px var(--gutter-s);
  background-color: var(--bg-primary);

  @media (${({ theme }): string => theme.screenSizes.el}) {
    padding: var(--gutter-s) var(--gutter-s);
  }
  @media (${({ theme }): string => theme.screenSizes.xxl}) {
    padding: var(--gutter-s) var(--gutter-m);
  }
`

const LogoImg = styled.img`
  position: relative;
  z-index: 2;
  width: 200px;
  @media (${({ theme }): string => theme.screenSizes.md}) {
    width: 240px;
  }
`

const StyledGatsbyLink = styled(ScrollLink)`
  transition: all ease 300ms;
  margin-left: var(--gutter-s);
  @media (${({ theme }): string => theme.screenSizes.lg}) {
    margin-left: var(--gutter-m);
  }
  @media (max-width: 992px) {
    margin: 10px 0px;
  }
  @media (max-width: 1279px) {
    margin: 10px 0px;
  }
  @media (max-width: 1366px) {
    margin-left: var(--gutter-s);
  }
`

const ContactPoints = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  margin-left: var(--gutter-s);

  #contact-email {
    grid-column: 1 / span 2;
  }

  @media (${({ theme }): string => theme.screenSizes.lg}) {
    margin-left: var(--gutter-m);
  }

  @media (max-width: 1279px) {
    margin: 10px 0px;
  }
`

const RefLink = styled.div`
  display: flex;
  align-items: center;
  background-color: var(--button-primary);
  color: var(--text-light);
  padding: 10px 20px;
  border-radius: 20px;
  transition: all ease 300ms;

  img {
    width: 15px;
    margin-right: 10px;
  }

  &:hover {
    background-color: var(--button-primary-hover);
    color: var(--text-light);
  }
`

const PhoneLink = styled(RefLink)``

const LinksContainer = styled.div`
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  font-size: 9px;

  @media (${({ theme }): string => theme.screenSizes.lg}) {
    font-size: 14px;
  }

  @media (max-width: 1279px) {
    position: fixed;
    flex-direction: column;
    justify-content: center;
    width: auto;
    height: auto;
    font-size: 16px;
    background-color: var(--bg-primary);
    transition: transform 300ms ease-in-out;
    width: 100vw;
    height: 100vh;
    transform: translate(-100%, 0%);
    overflow: hidden;

    ${StyledGatsbyLink} {
      transform: translate(-30px, 0px);
      opacity: 0;
    }

    &.active {
      transform: translate(0%, 0%);

      ${StyledGatsbyLink} {
        transform: translate(0%, 0%);
        opacity: 1;

        &:nth-child(1) {
          transition-delay: 200ms;
        }
        &:nth-child(2) {
          transition-delay: 300ms;
        }
        &:nth-child(3) {
          transition-delay: 400ms;
        }
        &:nth-child(4) {
          transition-delay: 500ms;
        }
      }

      ${RefLink} {
        transition: background-color ease 300ms 0ms, transform ease 300ms 600ms,
          opacity ease 300ms 600ms;
      }
    }
  }
`

const ToggleContainer = styled.div<{ className: string }>`
  display: block;
  background-color: transparent;
  user-select: none;

  &.cross path#top,
  &.cross path#bottom {
    stroke-dashoffset: -680px;
  }
  &.cross path#middle {
    stroke-dashoffset: -110px;
    stroke-dasharray: 1px 220px;
  }
  &:focus,
  &:active {
    background-color: transparent;
  }

  @media (${({ theme }): string => theme.screenSizes.el}) {
    display: none;
  }
`

const Toggle = styled.svg`
  display: none;
  position: absolute;
  z-index: 10;
  top: 10px;
  right: 0;
  width: 80px;
  height: 50px;
  cursor: pointer;
  transform: translate3d(0, 0, 0);

  path {
    fill: none;
    transition: stroke-dashoffset 0.4s cubic-bezier(0.25, -0.25, 0.75, 1.25),
      stroke-dasharray 0.4s cubic-bezier(0.25, -0.25, 0.75, 1.25);
    stroke-width: 25px;
    stroke-linecap: round;
    stroke: var(--text-dark);
    stroke-dashoffset: 0px;
  }

  path#top,
  path#bottom {
    stroke-dasharray: 240px 950px;
  }
  path#middle {
    stroke-dasharray: 240px 240px;
  }
  @media (max-width: 1279px) {
    display: block;
  }
`

const Header: React.FC = () => {
  const [menuActive, setMenuActive] = useState(false)

  const handleMenuActive = useCallback(() => {
    setMenuActive(active => !active)
  }, [])

  const onPageLinkClick = useCallback(() => {
    setMenuActive(false)
  }, [])

  return (
    <HeaderContainer>
      <StyledGatsbyLink
        clickCallback={onPageLinkClick}
        page="/"
        section="landing"
      >
        <LogoImg src={Logo} alt="Menlyn Maine Logo" />
      </StyledGatsbyLink>

      <LinksContainer className={menuActive ? "active" : ""}>
        <StyledGatsbyLink
          clickCallback={onPageLinkClick}
          page="/"
          section="apartments"
        >
          Apartments for sale
        </StyledGatsbyLink>
        <StyledGatsbyLink
          clickCallback={onPageLinkClick}
          page="/"
          section="neighbourhood"
        >
          Our neighbourhood
        </StyledGatsbyLink>
        <StyledGatsbyLink
          clickCallback={onPageLinkClick}
          page="/"
          section="technology"
        >
          Technology
        </StyledGatsbyLink>
        <StyledGatsbyLink clickCallback={onPageLinkClick} page="contact-us">
          Contact us
        </StyledGatsbyLink>
        <ContactPoints>
          <a id="contact-email" href="mailto:ilma.brink@pamgolding.co.za">
            <RefLink>
              Ilma.brink@pamgolding.co.za
              {/* <img src={BookIcon} alt="Menlyn Maine - book a viewing" /> */}
            </RefLink>
          </a>
          <a id="contact-phone-number" href="tel:0736729907">
            <PhoneLink>{"073 672 9907"}</PhoneLink>
          </a>
          <a id="contact-phone-number" href="tel:0127101700">
            <PhoneLink>{"012 710 1700"}</PhoneLink>
          </a>
        </ContactPoints>
      </LinksContainer>

      <ToggleContainer
        onClick={handleMenuActive}
        className={menuActive ? "cross" : ""}
      >
        <Toggle viewBox="0 0 800 600">
          <path
            d="M300,220 C300,220 520,220 540,220 C740,220 640,540 520,420 C440,340 300,200 300,200"
            id="top"
          ></path>
          <path d="M300,300 L540,300" id="middle"></path>
          <path
            d="M300,210 C300,210 520,210 540,210 C740,210 640,530 520,410 C440,330 300,190 300,190"
            id="bottom"
            transform="translate(480, 280) scale(1, -1) translate(-480, -318) "
          ></path>
        </Toggle>
      </ToggleContainer>
    </HeaderContainer>
  )
}

export default Header
